// ------------------------------------------------------------------------------------------------------------------------------------------
// index.ts | Loader | Inicializador principal del sistema de agentes públicos de Hellomatik
// v2 — ES module loading + terser minification
// rebuild trigger 2026-06-21: revert widget v2 -> main (header En linea + teaser 2s)
// ------------------------------------------------------------------------------------------------------------------------------------------

import { HttpClient } from "./services/HttpClient.js";
import { get_current_script, get_lang_id, get_show_button } from './helpers/utils.js';
import { injectChatColors } from './helpers/injectChatColors.js';
import { injectTextSizes } from './helpers/injectTextSizes.js';
import { injectTypingSpeed } from './helpers/injectTypingSpeed.js';
import { injectInactivityTimeout } from './helpers/injectInactivityTimeout.js';
import { injectSidebarPages } from './helpers/injectSidebarPages.js';
import { injectShowButton } from './helpers/injectShowButton.js';
import { resolveScopedConfigAliases } from '@hellomatik/shared/utils/resolveScopedConfigAliases.js';
import { buildWidgetEventDetail, buildWidgetInstanceId } from '@hellomatik/shared/utils/widgetScope.js';

type HellomatikLoaderWindow = Window & typeof globalThis & {
  __HELLOMATIK_LAST_ERROR__?: string;
  __HELLOMATIK_LOADER_BOOTSTRAP__?: Promise<void>;
  __HELLOMATIK_LOADER_BOOTSTRAPPED__?: boolean;
  __HELLOMATIK_LOAD_WIDGET_APP__?: () => Promise<void>;
  __HELLOMATIK_PENDING_STYLES__?: string[];
  __HELLOMATIK_OVERLAY_SUPPRESS_STYLES__?: boolean;
  __HELLOMATIK_REFRESH_CONFIG__?: () => Promise<boolean>;
  // Menu de inicio pedido en paralelo con la carga del widget, para que el bootstrap
  // pueda pintarlo sin esperar a React. Ver el bloque PREFETCH DEL MENU DE INICIO.
  __HELLOMATIK_START_PREFETCH__?: Promise<unknown>;
  __HELLOMATIK_SHADOW_ROOT__?: ShadowRoot;
  HELLOMATIK_USE_LOCAL_PROXY?: boolean;
  HELLOMATIK_CONFIG?: Record<string, any>;
  // La pagina del cliente, si el host la declara. Misma variable que lee el widget en coreService.
  HELLOMATIK_PAGE_URL?: string;
  // Idioma resuelto por este loader (url > embed > <html lang> > navegador > config).
  HELLOMATIK_LANG_ID?: string;
};

const globalWindow = window as HellomatikLoaderWindow;

const importLocalWidgetBootstrap = async(): Promise<void> => {
  // Construct at runtime so Rollup cannot follow the dev-only /@fs entry and
  // emit a second copy of the complete widget inside dist/loader.
  //
  // `__HELLOMATIK_LOCAL_WIDGET_ENTRY__` es una ruta del dev-server de Vite
  // (`/@fs/<ruta absoluta de la máquina que compiló>`) y el define la incrusta
  // SIN condición, así que viaja también al bundle desplegado. En dev-chat
  // quedaba literalmente "/@fs//app/apps/widget/bootstrap.ts" — la ruta dentro
  // del contenedor que hizo el build.
  //
  // Resolverla contra `window.location.origin` la apuntaba al origen de la
  // PÁGINA ANFITRIONA, donde ese fichero no existe: 404 y el widget no montaba
  // jamás. Es lo que dejaba girando la vista previa del configurador, que corre
  // el loader de dev-chat dentro de un iframe `srcdoc` del panel.
  //
  // Ahora todo se resuelve contra la URL DEL PROPIO LOADER: en desarrollo
  // apunta a su dev-server (aunque la página anfitriona viva en otro puerto) y
  // en un build desplegado carga el widget compilado que está a su lado. El
  // discriminante es el entry `.ts`, que solo sirve el dev-server de Vite: un
  // loader compilado siempre se llama `hellomatik-loader.js`.
  const loaderUrl    = import.meta.url;
  const servedByVite = /\/index\.ts(?:[?#]|$)/.test( loaderUrl );
  const entry        = servedByVite ? __HELLOMATIK_LOCAL_WIDGET_ENTRY__ : './hellomatik-widget.es.js';
  const localWidgetUrl = new URL( entry, loaderUrl ).href;
  await import( /* @vite-ignore */ localWidgetUrl );
};

const injectLegacyOverlaySuppressStyles = (): void => {
  if( globalWindow.__HELLOMATIK_OVERLAY_SUPPRESS_STYLES__ )
    return;

  globalWindow.__HELLOMATIK_OVERLAY_SUPPRESS_STYLES__ = true;

  if( !globalWindow.__HELLOMATIK_PENDING_STYLES__ )
    globalWindow.__HELLOMATIK_PENDING_STYLES__ = [];

  globalWindow.__HELLOMATIK_PENDING_STYLES__.push(
    '/* Hide legacy fullscreen overlay without mutating React DOM */\n' +
    ':host div[class~="fixed"][class~="inset-0"][class~="z-[9999]"][style*="chat-overlay-background"] {' +
    ' display: none !important; opacity: 0 !important; pointer-events: none !important;' +
    '}'
  );
};

const isSilentLoaderExit = ( error: unknown ): boolean => {
  if( !( error instanceof Error ) )
    return false;

  return error.message === 'preview-mode' || error.message === 'limit-reached';
};

const reportLoaderError = ( error: unknown, publicKey?: string ): void => {
  const normalized = error instanceof Error ? error : new Error( String( error ) );
  globalWindow.__HELLOMATIK_LAST_ERROR__ = normalized.message;
  const instanceId = buildWidgetInstanceId( { publicKey } );

  try {
    window.dispatchEvent( new CustomEvent( 'hellomatik:error', {
      detail: buildWidgetEventDetail( instanceId, publicKey, {
        source    : 'loader',
        message   : normalized.message,
        publicKey : publicKey || null,
      } )
    } ) );
  } catch {
    // noop
  }

  console.error( '[Hellomatik] Loader error', normalized );
};

const normalizeBoolean = ( value: unknown, fallback: boolean ): boolean => {
  if( value === undefined || value === null || value === '' )
    return fallback;

  if( typeof value === 'boolean' )
    return value;

  if( typeof value === 'number' )
    return value !== 0;

  if( typeof value === 'string' )
  {
    const normalized = value.trim().toLowerCase();
    if( ['true', '1', 'yes', 'on', 'si'].includes( normalized ) )
      return true;
    if( ['false', '0', 'no', 'off'].includes( normalized ) )
      return false;
  }

  return fallback;
};

const normalizeOpenMode = ( value: unknown ): 'side-panel' | 'full-view' => {
  const normalized = String( value || '' ).trim().toLowerCase();
  if( normalized === 'full-view' || normalized === 'fullscreen' || normalized === 'full' )
    return 'full-view';
  return 'side-panel';
};

const getRuntimeLangId = ( currentScript: HTMLScriptElement | null, fallback: string ): string => {
  const scriptLang = String( currentScript?.dataset.langId || '' ).trim().toLowerCase();
  const globalLang = String( ( window as any ).HELLOMATIK_LANG_ID || '' ).trim().toLowerCase();

  return globalLang || scriptLang || fallback;
};

// ?lang= explícito en la URL del host (SOLO el parámetro GET, sin el fallback de get_lang_id).
// Es la elección explícita del usuario y debe ganar sobre navigator/config.
const getExplicitUrlLang = (): string => {
  try {
    const urlLang = new URLSearchParams( window.location.search ).get( 'lang' );
    if( urlLang && /^[a-z]{2,3}$/.test( urlLang.trim().toLowerCase() ) )
      return urlLang.trim().toLowerCase();
  } catch { /* ignore */ }
  return '';
};

// Idioma DE LA PÁGINA, del <html lang>. Subtag de 2 letras ('en-GB' -> 'en').
//
// Es la señal semántica que ya emite cualquier web multiidioma bien hecha (y la
// exige el SEO), así que sirve para que el widget siga al idioma de la página
// SIN que el cliente tenga que variar el embed por plantilla: el mismo snippet
// vale para /es y para /en. Comprobado en tacticalcenter.es, que declara
// lang="es" en la portada y lang="en" en /en.
//
// Va por DEBAJO de data-lang-id (quien lo pone explícitamente manda) y por
// ENCIMA del navegador: el <html lang> dice en qué idioma está ESTA PÁGINA,
// mientras que navigator solo dice qué idioma prefiere el visitante. Para
// decidir en qué idioma hablar en una página concreta, gana la página.
const getDocumentLang = (): string => {
  try {
    const raw = String( document.documentElement.getAttribute( 'lang' ) || '' );
    const primary = raw.trim().toLowerCase().split( '-' )[0];
    return /^[a-z]{2}$/.test( primary ) ? primary : '';
  } catch { /* ignore */ }
  return '';
};

// Idioma primario del navegador como subtag de 2 letras ('es-ES' -> 'es'). '' si no hay uno válido.
const getNavigatorLang = (): string => {
  try {
    const nav = String( ( navigator.languages && navigator.languages[0] ) || navigator.language || '' );
    const primary = nav.trim().toLowerCase().split( '-' )[0];
    return /^[a-z]{2}$/.test( primary ) ? primary : '';
  } catch { /* ignore */ }
  return '';
};

const isLocalLoaderScript = ( scriptSrc: string ): boolean =>
  scriptSrc.includes( 'localhost:' ) ||
  scriptSrc.includes( '127.0.0.1:' ) ||
  scriptSrc.includes( '/index.ts' );

const bootstrapLoader = async(): Promise<void> => {
  let currentPublicKey = '';

  try
  {
  // ------------------------------------------------------------------------------------------------------------------------
  // Captura de datos
  // ------------------------------------------------------------------------------------------------------------------------

  // Capturamos el script actual (el que tiene data-public-key)
  const current_script = get_current_script();

  // Leemos los datos de la etiqueta Script y de la URL actual que está incluyendo este Script
  const public_key  = current_script?.dataset.publicKey;  // Ej: 69ac4fec-6a54-448c-9eac-fb97192d6d6b
  currentPublicKey  = public_key || '';
  const widgetInstanceId = buildWidgetInstanceId( { publicKey: public_key } );
  let runtimeWidgetInstanceId = widgetInstanceId;
  const script_src = current_script?.src || '';
  const use_local_proxy = isLocalLoaderScript( script_src );
  const script_lang = current_script?.dataset.langId;
  const lang_id     = get_lang_id( current_script ).trim().toLowerCase(); // Ej: 'en', 'es', 'fr', etc. (por defecto 'en')
  const show_button = get_show_button( current_script );  // Ej: true, false
  
  // Comprobamos que tenemos todos los datos requeridos
  if( !public_key )
    throw new Error( 'Public Key not found' );

  // ------------------------------------------------------------------------------------------------------------------------
  // Modo Preview: para el iframe de previsualización en la página de config
  // Salta autenticación y carga directamente el widget con valores por defecto
  // ------------------------------------------------------------------------------------------------------------------------

  const preview_mode = current_script?.dataset.preview === 'true';

  if( preview_mode )
  {
    const previewOpenMode = normalizeOpenMode( current_script?.dataset.openMode );
    const previewVisibleOnMobile = normalizeBoolean( current_script?.dataset.visibleOnMobile, true );
    const previewShowTeaser = normalizeBoolean( current_script?.dataset.showTeaser, false );
    const previewTeaserText = String( current_script?.dataset.teaserText || '' );
    const previewPosition = String( current_script?.dataset.position || 'bottom-right' );
    const parsedPreviewHGap = Number.parseInt( String( current_script?.dataset.horizontalGap || '' ), 10 );
    const parsedPreviewVGap = Number.parseInt( String( current_script?.dataset.verticalGap || '' ), 10 );
    const previewHGap = Number.isFinite( parsedPreviewHGap ) ? parsedPreviewHGap : 24;
    const previewVGap = Number.isFinite( parsedPreviewVGap ) ? parsedPreviewVGap : 24;
    const previewCompanyName = String( current_script?.dataset.companyName || '' );
    const existingPreviewConfig =
      ( typeof ( window as any ).HELLOMATIK_CONFIG === 'object' && ( window as any ).HELLOMATIK_CONFIG !== null )
        ? { ...( window as any ).HELLOMATIK_CONFIG }
        : {};

    // Establecer variables globales mínimas para que el widget renderice.
    // Importante: conservar la config ya inyectada por el configurator para que
    // el widget use los textos y contenidos reales del preview en lugar de defaults.
    ( window as any ).HELLOMATIK_PUBLIC_KEY        = public_key;
    ( window as any ).HELLOMATIK_LANG_ID           = lang_id;
    ( window as any ).HELLOMATIK_SHOW_BUTTON       = show_button;
    ( window as any ).HELLOMATIK_MODE              = 'local';
    ( window as any ).HELLOMATIK_VERSION           = 'preview';
    ( window as any ).HELLOMATIK_CONFIG            = existingPreviewConfig;
    ( window as any ).HELLOMATIK_WIDGET_INSTANCE_ID = widgetInstanceId;
    ( window as any ).HELLOMATIK_OPEN_MODE         = previewOpenMode;
    ( window as any ).HELLOMATIK_VISIBLE_ON_MOBILE = previewVisibleOnMobile;
    ( window as any ).HELLOMATIK_SHOW_TEASER       = previewShowTeaser;
    ( window as any ).HELLOMATIK_TEASER_TEXT       = previewTeaserText;
    ( window as any ).HELLOMATIK_POSITION          = previewPosition;
    ( window as any ).HELLOMATIK_H_GAP             = previewHGap;
    ( window as any ).HELLOMATIK_V_GAP             = previewVGap;
    ( window as any ).HELLOMATIK_COMPANY_NAME      = previewCompanyName;
    ( window as any ).HELLOMATIK_USE_LOCAL_PROXY   = true;

    // Tamaño de texto: el ÚNICO ajuste de apariencia que no viaja como variable
    // CSS. `font-size` (small/default/large) se traduce a reglas `.text-*` que
    // genera injectTextSizes(), y esa llamada vivía DESPUÉS de este retorno
    // temprano — o sea que en la vista previa del configurador no se ejecutaba
    // nunca y el ajuste no se veía aplicado (los colores y el interlineado sí,
    // porque son vars --chat-* que el panel inyecta en el Shadow DOM).
    //
    // Va ANTES de cargar el widget porque el CSS se encola en
    // __HELLOMATIK_PENDING_STYLES__ y el widget lo vuelca en su Shadow DOM al
    // montarse. El camino normal no cambia: allí se sigue llamando en su sitio.
    injectTextSizes();

    // Cargar el widget directamente sin pasar por auth ni config
    await importLocalWidgetBootstrap();
    await globalWindow.__HELLOMATIK_LOAD_WIDGET_APP__?.();

    // Salimos del loader aquí — no necesitamos auth, config ni inyecciones
    throw new Error( 'preview-mode' );
  }

  // ------------------------------------------------------------------------------------------------------------------------
  // Configuración de entorno y URLs
  // ------------------------------------------------------------------------------------------------------------------------

  // Capturar el modo desde variables de entorno (local, development, staging, production)
  const mode = import.meta.env.VITE_MODE || 'local';

  // Preconnect al CDN del widget mientras corre el /auth: calienta DNS+TLS para
  // el bundle y las fuentes (mismo origen). crossorigin porque los ES modules y
  // los woff2 se piden en modo CORS. Idempotente por id.
  const CHAT_ORIGINS: Record<string, string> = {
    production  : 'https://chat.hellomatik.com',
    staging     : 'https://staging-chat.hellomatik.com',
    development : 'https://dev-chat.hellomatik.com',
  };
  const chatOrigin = CHAT_ORIGINS[mode];
  if( chatOrigin && !document.getElementById( 'hellomatik-preconnect' ) )
  {
    const link = document.createElement( 'link' );
    link.id = 'hellomatik-preconnect';
    link.rel = 'preconnect';
    link.href = chatOrigin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild( link );
  }

  // Override para Core (permite usar staging/prod Core con widget local)
  const core_override_url = import.meta.env.VITE_CORE_OVERRIDE_URL || '';

  // Determinamos la URL del Core según el entorno
  let core_url: string;
  if( use_local_proxy )
  {
    core_url = '/auth';
  }
  else if( core_override_url && mode === 'local' )
  {
    // Override activo: usar URL externa para auth
    core_url = core_override_url + '/auth';
  }
  else
  {
    switch( mode )
    {
      case 'production':
        core_url = 'https://core.hellomatik.com/auth';
        break;
      case 'staging':
        core_url = 'https://staging-core.hellomatik.com/auth';
        break;
      case 'development':
        core_url = 'https://dev-core.hellomatik.com/auth';
        break;
      case 'local':
      default:
        core_url = 'https://core.hellomatik.loc/auth';
        break;
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------
  // Consulta al Core para verificar la existencia del agente
  // ------------------------------------------------------------------------------------------------------------------------

  // Inicializamos el cliente HTTP
  const http_client = new HttpClient();

  // ── Caché local de auth y config (medido en prod 11-ago-2026: auth 690ms +
  // config 189ms EN SERIE antes de poder pintar el launcher). El visitante
  // recurrente sirve ambos de localStorage y pinta al instante; en segundo
  // plano SIEMPRE se refresca contra el Core, así que un tenant desactivado,
  // un límite alcanzado o una config nueva se aplican con un page-view de
  // retraso como máximo. El access_token ya vive expuesto en la página (lo
  // usa el propio widget), guardarlo en localStorage no amplía la exposición.
  const AUTH_CACHE_TTL_MS   = 10 * 60 * 1000;
  const CONFIG_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  const auth_cache_key = `hm_auth_v1_${public_key}`;
  const readJsonCache = ( key: string ): Record<string, any> | null => {
    try {
      const raw = window.localStorage.getItem( key );
      if( !raw ) return null;
      const parsed = JSON.parse( raw );
      return ( typeof parsed === 'object' && parsed !== null ) ? parsed : null;
    } catch { return null; }
  };
  const writeJsonCache = ( key: string, value: unknown ): void => {
    try { window.localStorage.setItem( key, JSON.stringify( value ) ); } catch { /* storage bloqueado por el host */ }
  };
  const clearCache = ( key: string ): void => {
    try { window.localStorage.removeItem( key ); } catch { /* storage bloqueado por el host */ }
  };

  // Calculamos los parámetors del POST
  const body_data = {
    access_key: public_key,
    lang_id: lang_id
  };

  // Auth: caché primero, red si no hay. Con caché válida, el POST de auth se
  // lanza igualmente en segundo plano para revalidar y rodar la caché.
  let core_response: Record<string, any>;
  const cached_auth = readJsonCache( auth_cache_key );
  const cached_auth_ok = !!( cached_auth
    && ( Date.now() - Number( cached_auth.t || 0 ) ) < AUTH_CACHE_TTL_MS
    && cached_auth.response
    && cached_auth.response.access_token > ''
    && cached_auth.response.limit_reached !== true );

  if( cached_auth_ok )
  {
    core_response = cached_auth.response;
    void http_client.post( core_url, body_data ).then( ( fresh: Record<string, any> ) => {
      if( !fresh || fresh.limit_reached === true || !( fresh.access_token > '' ) ) {
        // Estado revocado/limitado: fuera cachés (auth Y config), el
        // siguiente page-view lo aplica.
        clearCache( auth_cache_key );
        try {
          const config_prefix = `hm_cfg_v1_${public_key}`;
          for( let i = window.localStorage.length - 1; i >= 0; i-- ) {
            const stored_key = window.localStorage.key( i );
            if( stored_key && stored_key.startsWith( config_prefix ) ) window.localStorage.removeItem( stored_key );
          }
        } catch { /* storage bloqueado por el host */ }
        return;
      }
      writeJsonCache( auth_cache_key, { t: Date.now(), response: fresh } );
    } ).catch( () => { /* la revalidación no debe romper nada */ } );
  }
  else
  {
    // Realizamos la petición al Core para comprobar que este agente está activado
    core_response = await http_client.post(
      core_url,
      body_data
    );
    if( core_response && core_response.access_token > '' && core_response.limit_reached !== true )
      writeJsonCache( auth_cache_key, { t: Date.now(), response: core_response } );
  }

  // Si se alcanzó el límite mensual de mensajes, no cargar el widget
  if( core_response.limit_reached === true )
  {
    if( import.meta.env.DEV ) console.warn( '[Hellomatik] Monthly message limit reached. Widget disabled.' );
    throw new Error( 'limit-reached' );
  }

  // Según la respuesta, cargamos el Script principal o no
  if( core_response.access_token > '' )
  {
    // Guardar el public_key, lang_id, show_button, mode y version en variables globales para que el widget pueda accederlos
    ( window as any ).HELLOMATIK_PUBLIC_KEY   = public_key;
    ( window as any ).HELLOMATIK_LANG_ID      = lang_id;
    ( window as any ).HELLOMATIK_SHOW_BUTTON  = show_button;
    ( window as any ).HELLOMATIK_MODE         = ( core_override_url && mode === 'local' ) ? 'development' : mode;
    ( window as any ).HELLOMATIK_USE_LOCAL_PROXY = use_local_proxy;
    ( window as any ).HELLOMATIK_VERSION      = core_response.version || 'unknown';
    
    // ------------------------------------------------------------------------------------------------------------------------
    // Obtener configuración del Core antes de cargar el widget
    // ------------------------------------------------------------------------------------------------------------------------
    
    // Determinar la URL del Core según el entorno
    let core_config_url: string;
    if( use_local_proxy )
    {
      core_config_url = '/core';
    }
    else if( core_override_url && mode === 'local' )
    {
      // Override activo: usar URL externa para config
      core_config_url = core_override_url + '/core';
    }
    else
    {
      switch( mode )
      {
        case 'production':
          core_config_url = 'https://core.hellomatik.com/core';
          break;
        case 'staging':
          core_config_url = 'https://staging-core.hellomatik.com/core';
          break;
        case 'development':
          core_config_url = 'https://dev-core.hellomatik.com/core';
          break;
        case 'local':
        default:
          // En local, hm_app_kernel solo tiene /app
          core_config_url = 'https://core.hellomatik.loc/core';
          break;
      }
    }

    // Configurar el cliente HTTP con el token de acceso
    http_client.set_headers( {
      'Authorization': `Bearer ${core_response.access_token}`
    } );
    
    // Realizar petición para obtener la configuración pública
    const buildConfigMap = ( configItems: any[] ): Record<string, any> => {
      const config_map: Record<string, any> = {};

      configItems.forEach( ( item: any ) => {
        if( typeof item.config_value === 'string' ) {
          try {
            const parsed = JSON.parse( item.config_value );
            config_map[item.config_id] = parsed;

            if( typeof parsed === 'object' && parsed !== null && !Array.isArray( parsed ) ) {
              const keys = Object.keys( parsed );
              const isLangObject = keys.length > 0 && keys.every( key => /^[a-z]{2,3}$/.test( key ) );

              if( isLangObject ) {
                keys.forEach( lang => {
                  config_map[`${item.config_id}-${lang}`] = parsed[lang];
                } );
              }
            }
          } catch {
            config_map[item.config_id] = item.config_value;
          }
        } else {
          config_map[item.config_id] = item.config_value;
        }
      } );

      return config_map;
    };

    const resolveLocalizedValue = ( value: unknown, langId: string ): string => {
      if( !value ) return '';
      if( typeof value === 'string' ) return value;
      if( typeof value === 'object' && value !== null && !Array.isArray( value ) ) {
        const obj = value as Record<string, unknown>;
        const lang = langId.trim().toLowerCase();
        if( typeof obj[lang] === 'string' ) return obj[lang] as string;
        for( const fallback of ['es', 'en', 'ca'] ) {
          if( fallback !== lang && typeof obj[fallback] === 'string' ) return obj[fallback] as string;
        }
        const first = Object.values( obj ).find( v => typeof v === 'string' && v.trim() !== '' );
        if( typeof first === 'string' ) return first;
      }
      return String( value );
    };

    // Dominios permitidos (config `domains` del canal Webchat): si la lista
    // existe y no está vacía, el widget SOLO se monta en esos dominios (match
    // exacto o subdominio; localhost siempre permitido para desarrollo).
    // Lista vacía/ausente = sin restricción. Esto es la 1ª barrera (cliente);
    // la 2ª (server) valida el Origin en el /auth del Core.
    let domain_blocked = false;

    const isDomainAllowed = ( config_map: Record<string, any> ): boolean => {
      const allowed = config_map['domains'];
      if( !Array.isArray( allowed ) || allowed.length === 0 ) return true;
      const host = window.location.hostname.toLowerCase().replace( /^www\./, '' );
      if( host === 'localhost' || host === '127.0.0.1' ) return true;
      return allowed.some( ( d ) => {
        const dom = String( d || '' ).toLowerCase().trim().replace( /^https?:\/\//, '' ).replace( /^www\./, '' ).split( '/' )[0];
        return dom !== '' && ( host === dom || host.endsWith( '.' + dom ) );
      } );
    };

    const applyConfigMapToGlobals = ( config_map: Record<string, any>, appendDesignCss: boolean ): void => {
      resolveScopedConfigAliases( config_map, [public_key, core_response.space_uuid] );

      if( !isDomainAllowed( config_map ) ) {
        console.warn( '[hellomatik] Widget no autorizado en este dominio (' + window.location.hostname + ').' );
        domain_blocked = true;
        return;
      }

      ( window as any ).HELLOMATIK_CONFIG = config_map;

      const config_lang = String( config_map['language'] || '' ).trim().toLowerCase();

      // available-langs de la config (array de códigos realmente soportados por este widget).
      const available_langs = Array.isArray( config_map['available-langs'] )
        ? ( config_map['available-langs'] as any[] ).map( ( l ) => String( l || '' ).trim().toLowerCase() ).filter( Boolean )
        : [];

      // Resolución de idioma (prioridad de mayor a menor):
      //   1) ?lang= explícito en la URL del host (elección del usuario)
      //   2) HELLOMATIK_LANG_ID global / data-lang-id del script (elección EXPLÍCITA
      //      del dueño de la web para ESTA página — p.ej. clinicaplanas.com/en/ pone
      //      data-lang-id="en"; debe ganar a la heurística del navegador. Con nav por
      //      encima, un visitante con Chrome en inglés veía el widget en inglés aunque
      //      el embed dijera es — visto en Planas dev)
      //   3) <html lang> de la página SI está en available-langs (la web declara
      //      en qué idioma está ESTA página: /en dice lang="en". Así el cliente
      //      pone el MISMO embed en todo el sitio y el widget sigue al idioma
      //      solo, sin plantillas distintas por versión)
      //   4) idioma del navegador (subtag 2 letras) SI está en available-langs
      //   5) language canónico de la config
      //   6) fallback ya calculado (lang_id)
      // navigator solo se usa si el idioma está soportado, para nunca caer en un idioma
      // sin textos. En preview no se llega aquí (retorno temprano en preview_mode arriba),
      // así que la config inyectada del panel manda su propio HELLOMATIK_LANG_ID.
      const url_lang     = getExplicitUrlLang();
      const doc_lang     = getDocumentLang();
      const doc_lang_ok  = doc_lang !== '' && available_langs.includes( doc_lang );
      const nav_lang     = getNavigatorLang();
      const nav_lang_ok  = nav_lang !== '' && available_langs.includes( nav_lang );
      // Fuente EXPLÍCITA del embed (data-lang-id), NO la global: la global ya la
      // escribió este mismo loader con el fallback de get_lang_id (que incluye
      // navigator), y usarla aquí contaminaría la prioridad script > navegador.
      const embed_lang   = String( script_lang || '' ).trim().toLowerCase();

      const resolved_lang_id =
        url_lang
        || embed_lang
        || ( doc_lang_ok ? doc_lang : '' )
        || ( nav_lang_ok ? nav_lang : '' )
        || config_lang
        || lang_id;
      ( window as any ).HELLOMATIK_LANG_ID = resolved_lang_id;
      runtimeWidgetInstanceId = buildWidgetInstanceId( {
        publicKey   : public_key,
        accountUuid : String( config_map['account-uuid'] || '' ),
        companyCode : String( config_map['company-code'] || '' ),
      } );
      ( window as any ).HELLOMATIK_WIDGET_INSTANCE_ID = runtimeWidgetInstanceId;

      const widget_position        = String( config_map['position'] || 'bottom-right' );
      const parsedHGap             = Number.parseInt( String( config_map['horizontal-gap'] ?? '' ), 10 );
      const parsedVGap             = Number.parseInt( String( config_map['vertical-gap'] ?? '' ), 10 );
      const widget_h_gap           = Number.isFinite( parsedHGap ) ? parsedHGap : 24;
      const widget_v_gap           = Number.isFinite( parsedVGap ) ? parsedVGap : 24;
      const widget_visible_mobile  = normalizeBoolean( config_map['visible-on-mobile'], true );
      const widget_open_mode       = normalizeOpenMode( config_map['open-mode'] );
      const widget_show_teaser     = normalizeBoolean( config_map['show-teaser'], false );
      const rawTeaser = config_map['teaser-text'];
      const widget_teaser_text     = typeof rawTeaser === 'string'
        ? rawTeaser
        : ( rawTeaser && typeof rawTeaser === 'object' )
          ? ( ( rawTeaser as Record<string, string> )[resolved_lang_id] || Object.values( rawTeaser as Record<string, string> ).find( v => typeof v === 'string' && v.length > 0 ) || '' )
          : String( rawTeaser || '' );
      const rawCompanyName = config_map['company-name'];
      const widget_company_name    = typeof rawCompanyName === 'string'
        ? rawCompanyName
        : ( rawCompanyName && typeof rawCompanyName === 'object' )
          ? ( ( rawCompanyName as Record<string, string> )[resolved_lang_id] || Object.values( rawCompanyName as Record<string, string> ).find( v => typeof v === 'string' && v.length > 0 ) || '' )
          : String( rawCompanyName || '' );
      const widget_use_agent       = normalizeBoolean( config_map['use-agent'], false );

      ( window as any ).HELLOMATIK_POSITION           = widget_position;
      ( window as any ).HELLOMATIK_H_GAP              = widget_h_gap;
      ( window as any ).HELLOMATIK_V_GAP              = widget_v_gap;
      ( window as any ).HELLOMATIK_VISIBLE_ON_MOBILE  = widget_visible_mobile;
      ( window as any ).HELLOMATIK_OPEN_MODE          = widget_open_mode;
      ( window as any ).HELLOMATIK_SHOW_TEASER        = widget_show_teaser;
      ( window as any ).HELLOMATIK_TEASER_TEXT        = widget_teaser_text;
      ( window as any ).HELLOMATIK_COMPANY_NAME       = widget_company_name;
      ( window as any ).HELLOMATIK_USE_AGENT          = widget_use_agent;

      if( !appendDesignCss )
        return;

      if( !( window as any ).__HELLOMATIK_PENDING_STYLES__ )
        ( window as any ).__HELLOMATIK_PENDING_STYLES__ = [];

      let designCss = '/* Hellomatik - Widget design config */\n';
      designCss += ':host {\n';
      // SECURITY: h_gap and v_gap are already validated as finite numbers (parseInt + isFinite).
      // position-h is derived from a strict includes check, not user input directly.
      designCss += `  --hm-h-gap: ${widget_h_gap}px;\n`;
      designCss += `  --hm-v-gap: ${widget_v_gap}px;\n`;
      designCss += `  --hm-position-h: ${widget_position.includes( 'left' ) ? 'left' : 'right'};\n`;
      designCss += '}\n';

      if( !widget_visible_mobile ) {
        designCss += '@media (max-width: 768px) {\n';
        designCss += '  :host { display: none !important; }\n';
        designCss += '}\n';
      }

      ( window as any ).__HELLOMATIK_PENDING_STYLES__.push( designCss );
    };

    const configCacheKey = (): string =>
      `hm_cfg_v1_${public_key}_${getRuntimeLangId( current_script, lang_id )}`;

    // Hilo que el widget dejó guardado en una visita anterior, si lo hay.
    //
    // Fuente PRIMERA: el snapshot de conversacion en sessionStorage ( hm_widget_convo_* ), que es de
    // donde el widget restaura al navegar y donde el thread_uuid esta SIEMPRE. La clave
    // hm_widget_thread_* de localStorage solo se escribe si el id de instancia ya estaba puesto
    // cuando se creo el hilo, asi que no se puede depender de ella — comprobado: en una conversacion
    // recien creada el snapshot tenia el hilo y localStorage no.
    //
    // El id de instancia puede llevar mas partes que la clave publica ( hmw-<key>--<account>--<code> ),
    // asi que se busca por PREFIJO — mismo patron que la limpieza de cachés de config de mas abajo.
    const storedThreadUuid = (): string => {
      const prefijo_convo  = `hm_widget_convo_hmw-${public_key}`;
      const prefijo_hilo   = `hm_widget_thread_hmw-${public_key}`;

      try {
        for( let i = 0; i < window.sessionStorage.length; i++ ) {
          const k = window.sessionStorage.key( i );
          if( !k || k.indexOf( prefijo_convo ) !== 0 ) continue;
          const parsed = JSON.parse( window.sessionStorage.getItem( k ) || '{}' );
          if( parsed && typeof parsed.threadUuid === 'string' && parsed.threadUuid )
            return parsed.threadUuid;
        }
      } catch { /* storage bloqueado o snapshot ilegible */ }

      try {
        for( let i = 0; i < window.localStorage.length; i++ ) {
          const k = window.localStorage.key( i );
          if( k && k.indexOf( prefijo_hilo ) === 0 )
            return window.localStorage.getItem( k ) || '';
        }
      } catch { /* storage bloqueado por el host */ }

      return '';
    };

    const fetchAndApplyPublicConfig = async( appendDesignCss: boolean ): Promise<boolean> => {
      try {
        const requested_lang_id = getRuntimeLangId( current_script, lang_id );

        // page_url + thread_uuid van A PROPOSITO, aunque la config no dependa de ninguno.
        //
        // Al navegar con una conversacion ya empezada, el widget la restaura de sessionStorage SIN
        // hablar con el servidor ( decision documentada: threads-get no es accesible desde el widget
        // publico ). La unica peticion que sale es esta, y salia ciega: ni pagina ni hilo. Con eso el
        // Core no puede saber que el cliente se ha movido, asi que @page_product no caducaba y el
        // chat seguia mostrando el producto de la ficha anterior — el maleton de Ivan, reproducido el
        // 14-ago yendo del cargador a los guantes. Solo se limpiaba al escribir un mensaje.
        //
        // Con los dos datos, el corte del Core se dispara en el propio arranque de la pagina.
        const thread_uuid = storedThreadUuid();

        const config_response = await http_client.post( core_config_url, {
          method: 'config',
          params: {
            lang_id: requested_lang_id,
            page_url: globalWindow.HELLOMATIK_PAGE_URL || window.location.href,
            ...( thread_uuid ? { thread_uuid } : {} )
          }
        } );
        if( config_response.result !== 1 || !config_response.data ) {
          globalWindow.HELLOMATIK_CONFIG = {};
          return false;
        }

        const config_map = buildConfigMap( config_response.data.config || [] );
        applyConfigMapToGlobals( config_map, appendDesignCss );
        writeJsonCache( configCacheKey(), { t: Date.now(), config: config_response.data.config || [] } );
        return true;
      } catch {
        globalWindow.HELLOMATIK_CONFIG = {};
        return false;
      }
    };

    // Exponer refresco de configuración para mantener el widget en sync
    // sin recargar la página host.
	    globalWindow.__HELLOMATIK_REFRESH_CONFIG__ = async(): Promise<boolean> => {
	      const refreshed = await fetchAndApplyPublicConfig( false );
	      if( refreshed ) {
	        injectShowButton();
	        // La ESCALA DE TEXTO también se re-inyecta: `font-size` no viaja como
	        // variable CSS de chat-colors, se traduce a reglas `.text-*` + vars
	        // `--hm-fs-*`, así que sin esta llamada un refresco que cambie el tamaño
	        // dejaba la cola de estilos con la escala vieja (y con ella la pastilla
	        // del shell, que la lee de `:host` antes de que React monte).
	        // La llamada es idempotente: reemplaza su propio bloque en la cola.
	        injectTextSizes();
	        window.dispatchEvent( new CustomEvent( 'hellomatik:config-updated', {
            detail: buildWidgetEventDetail( runtimeWidgetInstanceId, public_key )
          } ) );
	      }
	      return refreshed;
	    };

    // ── Precarga del bundle del widget EN PARALELO con la config ──
    // La URL del bundle no depende de la config (solo del modo y de la versión
    // del auth, que ya tenemos), así que el navegador puede ir descargándolo
    // mientras esperamos la respuesta de `config`. OJO: el import() de más
    // abajo NO se adelanta — ejecutar el bootstrap antes de tener la config
    // pintaría el launcher con defaults (justo el parpadeo que combatimos).
    // El <link rel="modulepreload"> descarga SIN ejecutar y el import
    // posterior golpea la caché. Tiene que ser la MISMA URL exacta o no hay
    // hit. Sin atributo crossorigin: el modo de credenciales por defecto de
    // modulepreload coincide con el del import() dinámico.
    let widget_base_url: string;
    switch( mode )
    {
      case 'production':
        widget_base_url = 'https://chat.hellomatik.com/widget';
        break;
      case 'staging':
        widget_base_url = 'https://staging-chat.hellomatik.com/widget';
        break;
      case 'development':
        widget_base_url = 'https://dev-chat.hellomatik.com/widget';
        break;
      case 'local':
      default:
        widget_base_url = ''; // Cargar desde fuentes locales
        break;
    }

    // En desarrollo local (script servido por localhost:5174), siempre usar widget local
    // para evitar desalineación con bundles remotos y asegurar que los fixes visuales
    // (como eliminación de overlays legacy) se apliquen inmediatamente.
    const should_use_local_widget = mode === 'local' || use_local_proxy;

    // Sin versión del Core, sello DIARIO (antes Math.random rompía la caché en
    // cada page-view: re-descarga del bundle entero en cada navegación).
    const widget_cache_version = core_response.version && core_response.version !== 'unknown'
      ? core_response.version
      : new Date().toISOString().slice( 0, 10 );
    const widget_module_url = `${widget_base_url}/hellomatik-widget.es.js?v=${widget_cache_version}`;

    if( !should_use_local_widget ) {
      const widget_preload = document.createElement( 'link' );
      widget_preload.rel = 'modulepreload';
      widget_preload.href = widget_module_url;
      document.head.appendChild( widget_preload );
    }

    // Config: con caché válida se aplica al instante (el launcher no espera
    // la red) y se refresca en segundo plano por el MISMO camino que ya usa
    // __HELLOMATIK_REFRESH_CONFIG__; sin caché, red y a esperar como siempre.
    const cached_config = readJsonCache( configCacheKey() );
    const cached_config_ok = !!( cached_config
      && Array.isArray( cached_config.config )
      && cached_config.config.length > 0
      && ( Date.now() - Number( cached_config.t || 0 ) ) < CONFIG_CACHE_TTL_MS );

    if( cached_config_ok )
    {
      applyConfigMapToGlobals( buildConfigMap( cached_config!.config ), true );
      void fetchAndApplyPublicConfig( false ).then( ( refreshed ) => {
        if( !refreshed ) return;
        injectShowButton();
        // Mismo caso que en __HELLOMATIK_REFRESH_CONFIG__: la revalidación en segundo
        // plano puede traer otro `font-size` y la escala hay que rehacerla (idempotente).
        injectTextSizes();
        window.dispatchEvent( new CustomEvent( 'hellomatik:config-updated', {
          detail: buildWidgetEventDetail( runtimeWidgetInstanceId, public_key )
        } ) );
      } ).catch( () => { /* la revalidación no debe romper nada */ } );
    }
    else
    {
      await fetchAndApplyPublicConfig( true );
    }

    // Dominio no autorizado → no montamos NADA (ni botón ni widget).
    if( domain_blocked ) return;

    // ------------------------------------------------------------------------------------------------------------------------
    // PREFETCH DEL MENU DE INICIO
    // ------------------------------------------------------------------------------------------------------------------------
    // El menu que ve el visitante al abrir el chat ( saludo + botones ) es el evento `start`, y hasta
    // ahora no se pedia hasta que React habia montado: primero 1,1 MB de bundle y DESPUES la peticion.
    // Medido en produccion sobre clinicaplanas.com: el lanzador esta a los 375-450 ms pero el bundle
    // no se pide hasta los 3,0-5,0 s ( espera deliberada de 2.500 ms desde el `load` de la web del
    // cliente ), asi que quien pulsa antes se come la descarga, el parseo y solo entonces la peticion.
    //
    // Aqui ya tenemos token y URL del Core, y estamos en paralelo con la carga del widget: se pide
    // ahora y se deja resuelto en una global. El bootstrap lo pinta sin esperar a React y AIChat lo
    // consume en vez de repetir la llamada. Si falla, no pasa nada: el camino de siempre sigue ahi.
    globalWindow.__HELLOMATIK_START_PREFETCH__ = ( async(): Promise<unknown> => {
      try {
        const respuesta = await http_client.post( core_config_url, {
          method: 'event',
          params: {
            event      : 'start',
            // SOLO LECTURA. Esto se pide en cada carga de pagina, abra o no el visitante el chat, asi
            // que no puede crear conversacion, ni dejar rastro en el historial, ni ejecutar el nodo de
            // arranque cuando ese nodo hace trabajo de verdad ( llamar a la tienda del tenant, evaluar
            // condiciones ). El Core lo corta en seco con esta bandera; sin ella, Tactical Center paso
            // de 4,5 a 77,8 conversaciones por hora el 6-sep-2026, 290 de 389 sin un solo mensaje.
            prefetch   : true,
            thread_uuid: storedThreadUuid() || '',
            lang_id    : String( globalWindow.HELLOMATIK_LANG_ID || lang_id || '' ),
            page_url   : globalWindow.HELLOMATIK_PAGE_URL || window.location.href,
            platform   : 'widget',
          }
        } );
        return ( respuesta && respuesta.result === 1 ) ? respuesta : null;
      } catch {
        return null;
      }
    } )();

    // ------------------------------------------------------------------------------------------------------------------------
    // Inyectar colores personalizados del chat ANTES de cargar el widget
    // ------------------------------------------------------------------------------------------------------------------------

    // Aplicar colores personalizados si existen en la configuración
    // IMPORTANTE: Esto debe ocurrir ANTES de cargar el CSS del widget
    injectChatColors();

    // ------------------------------------------------------------------------------------------------------------------------
    // Inyectar tamaños de texto aumentados ANTES de cargar el widget
    // ------------------------------------------------------------------------------------------------------------------------

    // Aumentar todos los tamaños de texto en 1px para mejor legibilidad
    // IMPORTANTE: Esto debe ocurrir ANTES de cargar el CSS del widget
    injectTextSizes();

    // ------------------------------------------------------------------------------------------------------------------------
    // Inyectar configuración de velocidad de escritura ANTES de cargar el widget
    // ------------------------------------------------------------------------------------------------------------------------

    // Aplicar velocidad de escritura personalizada si existe en la configuración
    // IMPORTANTE: Esto debe ocurrir ANTES de cargar el CSS del widget
    injectTypingSpeed();

    // ------------------------------------------------------------------------------------------------------------------------
    // Inyectar configuración de timeout de inactividad ANTES de cargar el widget
    // ------------------------------------------------------------------------------------------------------------------------

    // Aplicar timeout de inactividad personalizado si existe en la configuración
    // IMPORTANTE: Esto debe ocurrir ANTES de cargar el CSS del widget
    injectInactivityTimeout();

    // ------------------------------------------------------------------------------------------------------------------------
    // Inyectar configuración de páginas visibles en el sidebar
    // ------------------------------------------------------------------------------------------------------------------------

    // Aplicar configuración de páginas del sidebar si existe en la configuración
    injectSidebarPages();

    // ------------------------------------------------------------------------------------------------------------------------
    // Inyectar configuración de visibilidad del botón flotante
    // ------------------------------------------------------------------------------------------------------------------------

    // Aplicar configuración de show-button desde DB, sobrescribiendo el data-show-button del script si existe
    injectShowButton();

    // Compatibilidad: ocultar overlay legacy en bundles antiguos del widget
    // sin eliminar nodos del árbol React (evita desincronizaciones de render).
    injectLegacyOverlaySuppressStyles();

    // ------------------------------------------------------------------------------------------------------------------------
    // Cargar el widget
    // ------------------------------------------------------------------------------------------------------------------------

    const importWidget = async(): Promise<void> => {
      if( !should_use_local_widget )
      {
        // Producción/Staging: cargar widget como ES module (habilita code-splitting real)
        // ChartRenderer se carga lazy solo cuando se necesita (~250KB gzipped menos en carga inicial)
        // El loader ya se ejecuta como type="module", así que import() funciona nativamente.
        // La URL viene precalculada de ARRIBA (antes del fetch de config), donde
        // también se emitió el modulepreload: misma cadena, hit de caché.
        await import( /* @vite-ignore */ widget_module_url );
      }
      else
      {
        // Desarrollo local: solo cargar el widget (el CSS se importa dentro de index.tsx con ?inline)
        // NO importar index.css aquí porque se inyectaría en el <head> global
        await importLocalWidgetBootstrap();
      }

      globalWindow.__HELLOMATIK_LOADER_BOOTSTRAPPED__ = true;
    };

    // El entry remoto es ahora un bootstrap sin React que pinta el launcher de
    // inmediato. Él decide cuándo hidratar el chat completo (intent/full-view/
    // idle) y conserva/re-emite los eventos públicos tempranos.
    await importWidget();
  }
}
catch( error )
{
    globalWindow.__HELLOMATIK_LOADER_BOOTSTRAPPED__ = false;

    if( !isSilentLoaderExit( error ) )
      reportLoaderError( error, currentPublicKey );
  }
};

if( globalWindow.__HELLOMATIK_LOADER_BOOTSTRAPPED__ && globalWindow.__HELLOMATIK_SHADOW_ROOT__ ) {
  window.dispatchEvent( new CustomEvent( 'hellomatik:ready', {
    detail: buildWidgetEventDetail(
      String( window.HELLOMATIK_WIDGET_INSTANCE_ID || buildWidgetInstanceId( { publicKey: window.HELLOMATIK_PUBLIC_KEY } ) ),
      window.HELLOMATIK_PUBLIC_KEY,
      { source: 'loader' }
    )
  } ) );
} else if( globalWindow.__HELLOMATIK_LOADER_BOOTSTRAP__ ) {
  await globalWindow.__HELLOMATIK_LOADER_BOOTSTRAP__;
} else {
  const bootPromise = bootstrapLoader().finally( () => {
    delete globalWindow.__HELLOMATIK_LOADER_BOOTSTRAP__;
  } );

  globalWindow.__HELLOMATIK_LOADER_BOOTSTRAP__ = bootPromise;
  await bootPromise;
}
